<script setup lang="ts">
import { getToolName, type ToolUIPart } from "ai";
import CopilotSessionBatchResult from "~/components/Copilot/blocks/CopilotSessionBatchResult.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useFormats } from "~/composables/utils/useFormat";
import { CopilotToolEnum, CopilotToolStateEnum } from "~/utils/enums/copilot/chat.enums";
import {
  copilotToolLabelsOf,
  copilotToolSummaryOf,
  createSessionBatchResultOf,
  createSessionRetryAdviceOf,
  isCopilotToolPending,
  isCopilotToolRunning,
} from "~/utils/helpers/copilot/tool-action";
import type {
  ICopilotApproval,
  ICopilotToolLabels,
  ICopilotToolSummaryLine,
  ICopilotTools,
  ICreateSessionBatchResult,
} from "~/utils/interfaces/copilot-interfaces";

const props = withDefaults(
  defineProps<{
    part: ToolUIPart<ICopilotTools>;
    disabled?: boolean;
    /** Retry budget of the answer this action belongs to — spent, the failure stands as a record. */
    canRetry?: boolean;
  }>(),
  { disabled: false, canRetry: true },
);

const emit = defineEmits<{
  approve: [payload: ICopilotApproval];
  deny: [payload: ICopilotApproval];
  retry: [];
}>();

const { toDateLongDisplay } = useFormats();

const toolName = computed(() => getToolName(props.part) as CopilotToolEnum);
const summaryDeps = { toDateLongDisplay };

/*
 * `state`, `approval`, `input` and `output` are mutated in place by the AI SDK on the raw part
 * object, so the mutation never notifies Vue: a `computed` would cache what it read on the first
 * render — a half-streamed input, or an action never seen moving to "approval-requested". Plain
 * functions re-run on each render.
 */
function approvalIdOf(): string | undefined {
  return "approval" in props.part ? props.part.approval?.id : undefined;
}

function isPending(): boolean {
  return isCopilotToolPending(props.part.state as CopilotToolStateEnum);
}

function isRunning(): boolean {
  return isCopilotToolRunning(props.part.state as CopilotToolStateEnum);
}

/** Counts the sessions of a batch, so the title and the confirm button say how many one approval creates. */
function labelsOf(): ICopilotToolLabels | null {
  return copilotToolLabelsOf(toolName.value, props.part.input);
}

function summaryOf(): ICopilotToolSummaryLine[] {
  return copilotToolSummaryOf(toolName.value, props.part.input, summaryDeps);
}

/** A batch answers period by period; any other output keeps the card's usual footer. */
function batchResultOf(): ICreateSessionBatchResult | null {
  const output = "output" in props.part ? props.part.output : undefined;

  return createSessionBatchResultOf(output, props.part.input, summaryDeps);
}

/** A batch that threw leaves every period unknown: the check before a retry applies to all of them. */
function retryAdviceOf(): string | null {
  return createSessionRetryAdviceOf(props.part.input);
}
</script>

<template>
  <div v-if="labelsOf()" class="flex flex-col gap-3 rounded-lg border border-stroke-color bg-light-color p-4">
    <div class="flex items-center gap-2">
      <SvgIcon :name="labelsOf()!.icon" class="h-[16px] w-[16px] shrink-0 text-primary-blue-color" />
      <span class="body_demi text-primary-color">{{ labelsOf()!.title }}</span>
    </div>

    <div v-if="summaryOf().length" class="flex flex-col gap-1">
      <div v-for="line of summaryOf()" :key="line.label" class="flex items-center justify-between gap-3">
        <span class="body_regular text-secondary-color shrink-0">{{ line.label }}</span>
        <span class="body_med text-primary-color truncate">{{ line.value }}</span>
      </div>
    </div>

    <span v-if="isPending()" class="meta text-secondary-color">L'assistant prépare l'action…</span>

    <!-- One approval, whatever the number of sessions: the buttons answer the whole lot. -->
    <div v-else-if="part.state === CopilotToolStateEnum.APPROVAL_REQUESTED && approvalIdOf()" class="flex gap-2">
      <AppButton
        :label="labelsOf()!.confirm"
        icon="check"
        :disabled="disabled"
        @click="emit('approve', { approvalId: approvalIdOf()!, toolName })"
      />
      <AppButton
        type="outlined"
        variant="error"
        label="Refuser"
        icon="close"
        :disabled="disabled"
        @click="emit('deny', { approvalId: approvalIdOf()!, toolName })"
      />
    </div>

    <div v-else-if="isRunning()" class="flex items-center gap-2">
      <SvgIcon name="uploading" class="h-[16px] w-[16px] shrink-0 animate-spin text-primary-blue-color" />
      <span class="body_regular text-secondary-color">{{ labelsOf()!.running ?? "Action en cours…" }}</span>
    </div>

    <CopilotSessionBatchResult
      v-else-if="part.state === CopilotToolStateEnum.OUTPUT_AVAILABLE && batchResultOf()"
      :result="batchResultOf()!"
    />

    <div v-else-if="part.state === CopilotToolStateEnum.OUTPUT_AVAILABLE" class="flex items-center gap-2">
      <SvgIcon name="check" class="h-[16px] w-[16px] shrink-0 text-medium-green-color" />
      <span class="body_regular text-primary-color">Action effectuée.</span>
    </div>

    <span v-else-if="part.state === CopilotToolStateEnum.OUTPUT_DENIED" class="body_regular text-secondary-color">
      Action refusée.
    </span>

    <!-- The action ran and failed: said here, where it was approved, and offered again. -->
    <div v-else-if="part.state === CopilotToolStateEnum.OUTPUT_ERROR" class="flex flex-col items-start gap-2">
      <div class="flex items-center gap-2">
        <SvgIcon name="warning_error" class="h-[16px] w-[16px] shrink-0 text-primary-red-color" />
        <span class="body_regular text-primary-color">L'action n'a pas pu être effectuée.</span>
      </div>
      <span v-if="retryAdviceOf()" class="meta text-secondary-color">{{ retryAdviceOf() }}</span>
      <AppButton
        v-if="canRetry"
        type="link"
        label="Réessayer"
        icon="refresh"
        :disabled="disabled"
        @click="emit('retry')"
      />
    </div>
  </div>
</template>
